/**
 * LinkMeter (spike) — the per-user-per-agent rapport indicator (1->20), a monochrome
 * link-chain motif + a 20-segment bar + the progressive-unlock ladder. Presentational only.
 */
import React from 'react';
import { Link2, Lock, Check } from 'lucide-react';
import { UNLOCK_LADDER } from './sampleData';

const MAX = 20;

/** Which ladder entry is the CURRENT tier (highest unlocked at or below the level). */
function currentUnlock(level: number): string {
  const reached = UNLOCK_LADDER.filter((u) => u.level <= level);
  return reached.length ? reached[reached.length - 1].label : UNLOCK_LADDER[0].label;
}

const LinkMeter: React.FC<{ level: number; showLadder?: boolean }> = ({ level, showLadder }) => {
  const lv = Math.max(1, Math.min(MAX, level));
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Link2 className="w-4 h-4 text-secondary-900 dark:text-white" />
        <span className="text-sm font-semibold text-secondary-900 dark:text-white">Link {lv}</span>
        <span className="text-xs text-secondary-400">/ {MAX}</span>
        <span className="ml-auto text-[11px] text-secondary-400 truncate">{currentUnlock(lv)}</span>
      </div>

      {/* 20-segment chain bar */}
      <div className="flex items-center gap-[3px]" aria-label={`Link level ${lv} of ${MAX}`}>
        {Array.from({ length: MAX }, (_, i) => (
          <span
            key={i}
            className={`h-1.5 flex-1 rounded-full ${i < lv ? 'bg-secondary-900 dark:bg-white' : 'bg-secondary-200 dark:bg-secondary-700'}`}
          />
        ))}
      </div>

      {showLadder && (
        <ul className="mt-2 space-y-1.5">
          {UNLOCK_LADDER.map((u) => {
            const unlocked = lv >= u.level;
            return (
              <li key={u.level} className="flex items-center gap-2 text-xs">
                {unlocked
                  ? <Check className="w-3.5 h-3.5 text-secondary-900 dark:text-white shrink-0" />
                  : <Lock className="w-3.5 h-3.5 text-secondary-300 dark:text-secondary-600 shrink-0" />}
                <span className={unlocked ? 'text-secondary-700 dark:text-secondary-200' : 'text-secondary-400'}>
                  {u.label}
                </span>
                {!unlocked && <span className="ml-auto text-[10px] text-secondary-400">Link {u.level}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LinkMeter;
